// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import getCountryCodes from '../../translations/countryCodes';
import { requireListingImage } from '../../util/configHelpers';
import { userInitialsAsString } from '../../util/data';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';

import {
  AspectRatioWrapper,
  FavoriteButton,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
    isIso,
    isOnSale,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  // Nextmine: "Free shipping"-badge direkte på billedet, så køberen ser det straks
  const { freeShippingNational, freeShippingInternational } = listing?.attributes?.publicData || {};
  const hasFreeShipping = !!(freeShippingNational || freeShippingInternational);

  return (
    <AspectRatioWrapper
      className={aspectRatioClass}
      boxClassName={css.imageMat}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
      {isIso ? (
        <span className={css.wantedBadge}>
          <FormattedMessage id="ListingCard.wantedBadge" />
        </span>
      ) : null}
      {/* Nextmine: "Sale"-badge når sælger har sat en før-pris højere end prisen */}
      {isOnSale && !isIso ? (
        <span className={css.saleBadge}>
          <FormattedMessage id="ListingCard.saleBadge" />
        </span>
      ) : null}
      {hasFreeShipping && !isIso ? (
        <span className={css.freeShippingBadge}>
          <FormattedMessage id="ListingCard.freeShippingBadge" />
        </span>
      ) : null}
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.aspectRatioClassName custom className for AspectRatioWrapper component
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    lazyLoadImage = true,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
    isOnSale,
    originalPriceFormatted,
  } = translations;

  const classes = classNames(rootClassName || css.root, className);

  const id = listing?.id?.uuid;
  const { title = '', publicData } = listing?.attributes || {};
  const slug = createSlug(title);

  // Nextmine: "Sold by {sælger}, {land}" fra sælgers profil + offentlige land
  const sellerCountryCode = listing?.author?.attributes?.profile?.publicData?.country;
  const sellerCountryName = sellerCountryCode
    ? getCountryCodes(intl.locale).find(c => c.code === sellerCountryCode)?.name ||
      sellerCountryCode
    : null;
  // Nextmine: sælgeren vises kun med initialer.
  const sellerName = userInitialsAsString(listing?.author, null);
  const soldByLine =
    sellerName && sellerCountryName
      ? intl.formatMessage(
          { id: 'ListingCard.soldByWithCountry' },
          { name: sellerName, country: sellerCountryName }
        )
      : sellerName
      ? intl.formatMessage({ id: 'ListingCard.soldBy' }, { name: sellerName })
      : sellerCountryName
      ? intl.formatMessage({ id: 'ListingCard.shipsFrom' }, { country: sellerCountryName })
      : null;

  const { listingType, cardStyle } = publicData || {};
  const isIso = listingType === 'in-search-of';
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      {showListingImage ? (
        <ListingCardImage
          renderSizes={renderSizes}
          title={titlePlain}
          listing={listing}
          setActivePropsMaybe={setActivePropsMaybe}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          variantPrefix={variantPrefix}
          aspectRatioClassName={aspectRatioClassName}
          lazyLoadImage={lazyLoadImage}
          isIso={isIso}
          isOnSale={isOnSale}
        />
      ) : (
        <ListingCardThumbnail
          style={cardStyle}
          listingTitle={title}
          className={aspectRatioClassName}
          width={aspectWidth}
          height={aspectHeight}
          setActivePropsMaybe={setActivePropsMaybe}
        />
      )}
      {id ? (
        <FavoriteButton
          listingId={id}
          authorId={listing?.author?.id?.uuid}
          className={css.favoriteButton}
        />
      ) : null}
      <div className={css.info}>
        {isIso ? (
          <div className={css.price} title={priceTooltip}>
            {/* Budget er valgfrit — uden pris på annoncen vises "Open budget" */}
            {showPrice && listing?.attributes?.price && priceMessage ? (
              <FormattedMessage id="ListingCard.wantedBudget" values={{ price: priceMessage }} />
            ) : (
              <FormattedMessage id="ListingCard.wantedBudgetOpen" />
            )}
          </div>
        ) : showPrice ? (
          <div className={css.price} title={priceTooltip}>
            {/* Nextmine: udsalg - gennemstreget før-pris foran den aktuelle pris */}
            {isOnSale && originalPriceFormatted ? (
              <>
                <span className={css.originalPrice}>{originalPriceFormatted}</span>
                <span className={css.salePrice}>{priceMessage}</span>
              </>
            ) : (
              priceMessage
            )}
          </div>
        ) : null}
        <div className={css.mainInfo}>
          {showListingImage && (
            <div className={classNames(css.title, { [css.lightText]: darkMode })}>
              {titleFormatted}
            </div>
          )}
          {/* Nextmine: vis IKKE forfatter-linjen når by-linjen allerede har navnet
              ("Sold by Jen, …" / "Requested by Melanie") — ellers står navnet dobbelt */}
          {showAuthorInfo && !soldByLine && !(isIso && sellerName) ? (
            <div className={classNames(css.authorInfo, { [css.lightText]: darkMode })}>
              {authorName}
            </div>
          ) : null}
          {isIso && sellerName ? (
            <div className={classNames(css.shipsFrom, { [css.lightText]: darkMode })}>
              <FormattedMessage id="ListingCard.requestedBy" values={{ name: sellerName }} />
            </div>
          ) : !isIso && soldByLine ? (
            <div className={classNames(css.shipsFrom, { [css.lightText]: darkMode })}>
              {soldByLine}
            </div>
          ) : null}
        </div>
      </div>
    </NamedLink>
  );
};

export default ListingCard;
